import * as React from 'react';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import SupportRoundedIcon from '@mui/icons-material/SupportRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Tooltip from '@mui/joy/Tooltip';
import { Popper } from '@mui/base/Popper';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useColorScheme } from '@mui/joy/styles';

import ColorSchemeToggle from './ColorSchemeToggle';
import { closeSidebar } from '../utils';
import { getCurrentUser, logout } from '../services/api';
import { DarkSidebarStyles } from './darkSidebarStyles';
import EcobalanceLogo from './EcobalanceLogo';
import AlphaTag from './AlphaTag';

/**
 * Componente Toggler para seções expansíveis na barra lateral
 */
function Toggler({
  defaultExpanded = false,
  renderToggle,
  children,
}: {
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultExpanded);
  return (
    <React.Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={[
          {
            display: 'grid',
            transition: '0.2s ease',
            '& > *': {
              overflow: 'hidden',
            },
          },
          open ? { gridTemplateRows: '1fr' } : { gridTemplateRows: '0fr' },
        ]}
      >
        {children}
      </Box>
    </React.Fragment>
  );
}

type SidebarProps = {
  onNavigate?: (page: string) => void;
  currentPage?: string;
};

/**
 * Componente de barra lateral que fornece navegação com estado recolhível
 * 
 * Funcionalidades:
 * - Barra lateral expansível/recolhível
 * - Tooltips para o estado recolhido
 * - Submenus flutuantes no estado recolhido
 * - Navegação aninhada com menus dropdown
 * - Sempre mantém a aparência do tema escuro independente do tema global
 */
export default function Sidebar({ onNavigate, currentPage = 'dashboard' }: SidebarProps) {
  const location = useLocation();
  const currentUser = getCurrentUser();
  const activePath = location.pathname.substring(1) || 'dashboard';
  const { mode } = useColorScheme();
  
  const [isCustomMobile, setIsCustomMobile] = React.useState(false);
  
  // Função para detectar se estamos em um submódulo e determinar o módulo pai
  const getParentModuleSelection = (path: string) => {
    // Quebra o path para considerar subpaths como "clientes/cadastro", "clientes/editar/123"
    const pathSegments = path.split('/');
    const mainPath = pathSegments[0];
    
    const cadastrosSubmodules = ['clientes', 'propriedades'];
    
    if (cadastrosSubmodules.includes(mainPath)) {
      return 'cadastros';
    }
    return null;
  };

  // Função para determinar se o menu de ratificação deve estar ativo
  const isRatificacaoMenuActive = (path: string) => {
    // Verifica se está em qualquer página relacionada a ratificação
    return path === 'ratificacoes' || 
           path === 'nova-ratificacao' || 
           path.startsWith('ratificacoes/');
  };

  // Função para determinar se o menu financeiro deve estar ativo
  const isFinanceiroMenuActive = (path: string) => {
    // Verifica se está em qualquer página relacionada ao financeiro
    return path === 'financeiro' || 
           path.startsWith('financeiro/');
  };
  
  const parentModule = getParentModuleSelection(activePath);
  
  // Determina se um botão deve aparecer como selecionado no modo collapsed
  const isSelectedInCollapsed = (moduleName: string) => {
    if (!collapsed) return false;
    return parentModule === moduleName;
  };
  
  // Estados principais
  const [collapsed, setCollapsed] = React.useState(() => {
    // Recupera o estado da sidebar do localStorage
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [showToggleButton, setShowToggleButton] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = width / height;
      
      const isMobile = 
        width < 1200 || 
        aspectRatio < 1.3 || 
        height < 600 ||
        (width < 1400 && height < 800);
      
      setIsCustomMobile(isMobile);
      
      if (isMobile) {
        setCollapsed(true);
        localStorage.setItem('sidebarCollapsed', 'true');
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--SideNavigation-slideIn', '0');
          document.body.style.removeProperty('overflow');
        }
      } else {
        // Em desktop, restaurar preferência do usuário
        const savedState = localStorage.getItem('sidebarCollapsed');
        const shouldCollapse = savedState ? JSON.parse(savedState) : false;
        setCollapsed(shouldCollapse);
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--SideNavigation-slideIn', '0');
          document.body.style.removeProperty('overflow');
        }
      }
    };

    // Verificar na montagem
    handleResize();
    
    // Adicionar listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (isCustomMobile) {
      const forceDisableOverlay = () => {
        if (typeof document !== 'undefined') {
          const currentValue = document.documentElement.style.getPropertyValue('--SideNavigation-slideIn');
          if (currentValue !== '0') {
            document.documentElement.style.setProperty('--SideNavigation-slideIn', '0');
            document.body.style.removeProperty('overflow');
          }
        }
      };

      // Forçar imediatamente
      forceDisableOverlay();

      // Monitor constante a cada 100ms no modo mobile
      const interval = setInterval(forceDisableOverlay, 100);

      return () => clearInterval(interval);
    }
  }, [isCustomMobile]);
  
  /**
   * Refs e estados para os submenus flutuantes
   */
  const cadastrosAnchorRef = React.useRef(null);
  const [cadastrosOpen, setCadastrosOpen] = React.useState(false);
  const [mouseInCadastrosMenu, setMouseInCadastrosMenu] = React.useState(false);
  const cadastrosTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  /**
   * Alterna o estado recolhido da barra lateral
   */
  const toggleCollapse = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    // Salva o estado no localStorage
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
  };

  /**
   * Manipula o clique no ícone de pesquisa no estado recolhido
   */
  const handleSearchClick = () => {
    if (collapsed) {
      setCollapsed(false);
      // Atualiza o localStorage quando a sidebar é expandida pelo ícone de pesquisa
      localStorage.setItem('sidebarCollapsed', 'false');
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 300);
    }
  };

  /**
   * Estilos compartilhados para botões no estado recolhido
   */
  const collapsedButtonStyle = {
    justifyContent: 'center',
    alignItems: 'center',
    height: '40px',
    width: '40px',
    minHeight: '40px',
    minWidth: '40px',
    maxWidth: '40px',
    padding: 0,
    borderRadius: '8px',
    mx: 'auto',
    position: 'relative',
    zIndex: 1,
  };

  const buttonWithFullHover = {
    ...collapsedButtonStyle,
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '8px',
      zIndex: -1,
    },
  };

  const popperStyles = {
    p: 0.75,
    boxShadow: 'sm',
    borderRadius: '6px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'var(--joy-palette-background-surface)',
    minWidth: '180px',
  };
  
  const tooltipStyle = {
    zIndex: 10002,
    '--joy-shadowRing': 'none',
    '--Tooltip-background': '#121212',
    '--Tooltip-color': '#ffffff',
    '--Tooltip-borderRadius': '6px',
    borderRadius: '6px',
    overflow: 'hidden',
    bgcolor: '#121212',
    color: '#ffffff',
    border: '1px solid rgba(104, 112, 118, 0.2)',
    boxShadow: 'sm',
  };

  /**
   * Manipuladores de eventos para o menu flutuante de Cadastros
   */
  const handleCadastrosMouseEnter = () => {
    if (cadastrosTimerRef.current) {
      clearTimeout(cadastrosTimerRef.current);
      cadastrosTimerRef.current = null;
    }
    
    setCadastrosOpen(true);
  };

  const handleCadastrosMouseLeave = () => {
    if (!mouseInCadastrosMenu) {
      cadastrosTimerRef.current = setTimeout(() => {
        setCadastrosOpen(false);
      }, 300);
    }
  };

  const handleCadastrosMenuMouseEnter = () => {
    setMouseInCadastrosMenu(true);
    if (cadastrosTimerRef.current) {
      clearTimeout(cadastrosTimerRef.current);
      cadastrosTimerRef.current = null;
    }
  };

  const handleCadastrosMenuMouseLeave = () => {
    setMouseInCadastrosMenu(false);
    cadastrosTimerRef.current = setTimeout(() => {
      setCadastrosOpen(false);
    }, 300);
  };



  return (
    <Sheet
      className="Sidebar dark-sidebar"
      sx={{
        position: isCustomMobile ? 'fixed' : 'sticky',
        transform: isCustomMobile ? 'none' : 'none',
        transition: 'transform 0.4s, width 0.3s ease',
        zIndex: 10000,
        height: '100dvh',
        width: collapsed ? 'var(--Sidebar-width-collapsed)' : 'var(--Sidebar-width)',
        top: 0,
        p: 2,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRight: '1px solid',
        borderColor: 'divider',
        '&:hover': { '& .CollapseButton': { opacity: 1 } },
      }}
      onMouseEnter={() => setShowToggleButton(true)}
      onMouseLeave={() => setShowToggleButton(false)}
      onClick={(e) => {
        if (isCustomMobile) {
          e.stopPropagation();
          // Garantir que overlay permaneça desabilitado
          if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty('--SideNavigation-slideIn', '0');
          }
        }
      }}
    >
      <DarkSidebarStyles />
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Sidebar-width': '220px',
            '--Sidebar-width-collapsed': '72px',
            [theme.breakpoints.up('lg')]: {
              '--Sidebar-width': '240px',
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          display: isCustomMobile ? 'none !important' : 'block',
          position: 'fixed',
          zIndex: isCustomMobile ? -1 : 9998, // Z-index negativo força ficar atrás
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          opacity: isCustomMobile ? '0 !important' : 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: 'translateX(-100%)', // Sempre escondido
          pointerEvents: isCustomMobile ? 'none' : 'auto', // Desabilitar eventos no mobile
        }}
        onClick={() => {
          if (!isCustomMobile) {
            closeSidebar();
          }
        }}
      />
      
      {/* Botão de recolher/expandir - só aparece em desktop */}
      {showToggleButton && !isCustomMobile && (
        <IconButton
          className="CollapseButton"
          variant="soft"
          color="neutral"
          size="sm"
          onClick={toggleCollapse}
          sx={{
            position: 'absolute',
            right: '-16px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10001,
            borderRadius: '50%',
            backgroundColor: 'background.surface',
            boxShadow: 'sm',
            opacity: 0,
            transition: 'opacity 0.3s',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      )}
      
      {/* Seção do logotipo */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease'
      }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <IconButton 
            variant="plain" 
            className="logo-button"
            sx={{ 
              transition: 'all 0.3s ease',
              ...(collapsed && collapsedButtonStyle),
              backgroundColor: 'transparent !important',
              '&:hover': { 
                backgroundColor: 'transparent !important' 
              }
            }}
          >
            <EcobalanceLogo 
              size={collapsed ? 26 : 22}
            />
          </IconButton>
          <AlphaTag collapsed={collapsed} />
        </Box>
        {!collapsed && (
          <>
            <Typography level="title-lg" sx={{ ml: 1 }}>Ecobalance</Typography>
            <ColorSchemeToggle sx={{ ml: 'auto' }} />
          </>
        )}
      </Box>
      
      {/* Seção de pesquisa */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'center',
        overflow: 'visible'
      }}>
        {collapsed ? (
          <Tooltip
            title="Pesquisar"
            placement="right"
            variant="soft"
            className="dark-sidebar-tooltip"
            sx={tooltipStyle}
          >
            <IconButton
              size="md"
              onClick={handleSearchClick}
              sx={buttonWithFullHover}
            >
              <SearchRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Input 
            size="sm" 
            startDecorator={<SearchRoundedIcon />} 
            placeholder="Pesquisar" 
            className="search-input"
            slotProps={{
              input: {
                ref: searchInputRef,
                className: 'search-input-field'
              }
            }}
            sx={{ 
              width: '100%',
              "--Input-placeholderOpacity": 1
            }}
          />
        )}
      </Box>
      
      {/* Navegação principal */}
      <Box
        sx={{
          minHeight: 0,
          overflow: 'visible',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
            transition: 'all 0.3s ease',
            width: '100%',
            '&:hover': {
              overflow: 'visible',
            },
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: collapsed ? 2 : 1,
            '--List-nestedInsetStart': collapsed ? '0px' : '30px',
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
            width: '100%',
            px: 0,
            pt: 0,
            pb: collapsed ? 1 : 0,
            overflow: 'visible',
            ...(collapsed && {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }),
          }}
        >
          {/* Item Dashboard */}
          <ListItem 
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: collapsed ? 0 : undefined,
              px: 0,
              overflow: 'visible',
            }}
          >
            {collapsed ? (
              <Tooltip
                title="Início"
                placement="right"
                variant="soft"
                className="dark-sidebar-tooltip"
                sx={tooltipStyle}
              >
                <ListItemButton 
                  selected={activePath === 'home'}
                  component={RouterLink}
                  to="/home"
                  sx={buttonWithFullHover}
                >
                  <HomeRoundedIcon sx={{ fontSize: 22 }} />
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton 
                selected={activePath === 'home'}
                component={RouterLink}
                to="/home"
                sx={{ 
                  width: '100%',
                  mx: 0
                }}
              >
                <HomeRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Início</Typography>
                </ListItemContent>
              </ListItemButton>
            )}
          </ListItem>

          {/* Submenu Cadastros */}
          <ListItem 
            nested
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: collapsed ? 0 : undefined,
              px: 0,
              overflow: 'visible',
            }}
          >
            {collapsed ? (
              <Box sx={{ position: 'relative' }}>
                <ListItemButton 
                  ref={cadastrosAnchorRef}
                  selected={isSelectedInCollapsed('cadastros')}
                  sx={buttonWithFullHover}
                  onMouseEnter={handleCadastrosMouseEnter}
                  onMouseLeave={handleCadastrosMouseLeave}
                >
                  <FolderRoundedIcon sx={{ fontSize: 22 }} />
                </ListItemButton>
                
                <Popper 
                  open={cadastrosOpen} 
                  anchorEl={cadastrosAnchorRef.current}
                  placement="right-start"
                  disablePortal
                  className="dark-sidebar-popper"
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                  style={{ zIndex: 10003 }}
                >
                  <Sheet
                    variant="outlined"
                    sx={{
                      boxShadow: 'sm',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'var(--joy-palette-background-surface)',
                      minWidth: '180px',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={handleCadastrosMenuMouseEnter}
                    onMouseLeave={handleCadastrosMenuMouseLeave}
                  >
                    <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                      <Typography level="title-sm" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                        Cadastros
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ width: '100%' }}>
                      <Box
                        onClick={() => onNavigate && onNavigate('clientes')}
                        className="submenu-item"
                        sx={{
                          py: 0.5,
                          px: 1.5,
                          width: '100%',
                          cursor: 'pointer',
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          ...(activePath === 'clientes' && {
                            bgcolor: 'var(--joy-palette-primary-softBg)',
                            color: 'var(--joy-palette-primary-softColor)',
                          }),
                          '&:hover': {
                            bgcolor: 'var(--joy-palette-neutral-softHoverBg)',
                          },
                        }}
                      >
                        Clientes
                      </Box>
                      <Box
                        onClick={() => onNavigate && onNavigate('propriedades')}
                        className="submenu-item"
                        sx={{
                          py: 0.5,
                          px: 1.5,
                          width: '100%',
                          cursor: 'pointer',
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          ...(activePath === 'propriedades' && {
                            bgcolor: 'var(--joy-palette-primary-softBg)',
                            color: 'var(--joy-palette-primary-softColor)',
                          }),
                          '&:hover': {
                            bgcolor: 'var(--joy-palette-neutral-softHoverBg)',
                          },
                        }}
                      >
                        Propriedades
                      </Box>
                    </Box>
                  </Sheet>
                </Popper>
              </Box>
            ) : (
              <Toggler
                defaultExpanded={activePath === 'clientes' || activePath === 'propriedades'}
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton 
                    onClick={() => setOpen(!open)}
                    sx={{ 
                      width: '100%',
                      mx: 0
                    }}
                  >
                    <FolderRoundedIcon />
                    <ListItemContent>
                      <Typography level="title-sm">Cadastros</Typography>
                    </ListItemContent>
                    <KeyboardArrowDownIcon
                      sx={[
                        open
                          ? {
                              transform: 'rotate(180deg)',
                            }
                          : {
                              transform: 'none',
                            },
                      ]}
                    />
                  </ListItemButton>
                )}
              >
                {!collapsed && (
                  <List sx={{ 
                    gap: 0.5, 
                    width: '100%',
                    pl: 2,
                    pr: 0
                  }}>
                    <ListItem sx={{ 
                      mt: 0.5,
                      px: 0
                    }}>
                      <ListItemButton
                        selected={activePath === 'clientes'}
                        component={RouterLink}
                        to="/clientes"
                        sx={{ 
                          width: '100%',
                          mx: 0
                        }}
                      >
                        <ListItemContent>
                          <Typography level="title-sm">Clientes</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                    <ListItem sx={{ 
                      px: 0
                    }}>
                      <ListItemButton
                        selected={activePath === 'propriedades'}
                        component={RouterLink}
                        to="/propriedades"
                        sx={{ 
                          width: '100%',
                          mx: 0
                        }}
                      >
                        <ListItemContent>
                          <Typography level="title-sm">Propriedades</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  </List>
                )}
              </Toggler>
            )}
          </ListItem>

          {/* Item Ratificações */}
          <ListItem 
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: collapsed ? 0 : undefined,
              px: 0,
              overflow: 'visible',
            }}
          >
            {collapsed ? (
              <Tooltip
                title="Ratificações"
                placement="right"
                variant="soft"
                className="dark-sidebar-tooltip"
                sx={tooltipStyle}
              >
                <ListItemButton 
                  selected={isRatificacaoMenuActive(activePath)}
                  component={RouterLink}
                  to="/ratificacoes"
                  sx={buttonWithFullHover}
                >
                  <FactCheckIcon sx={{ fontSize: 22 }} />
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton 
                selected={isRatificacaoMenuActive(activePath)}
                component={RouterLink}
                to="/ratificacoes"
                sx={{ 
                  width: '100%',
                  mx: 0
                }}
              >
                <FactCheckIcon />
                <ListItemContent>
                  <Typography level="title-sm">Ratificações</Typography>
                </ListItemContent>
              </ListItemButton>
            )}
          </ListItem>

          {/* Item Financeiro */}
          <ListItem 
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: collapsed ? 0 : undefined,
              px: 0,
              overflow: 'visible',
            }}
          >
            {collapsed ? (
              <Tooltip
                title="Financeiro"
                placement="right"
                variant="soft"
                className="dark-sidebar-tooltip"
                sx={tooltipStyle}
              >
                <ListItemButton 
                  selected={isFinanceiroMenuActive(activePath)}
                  component={RouterLink}
                  to="/financeiro"
                  sx={buttonWithFullHover}
                >
                  <AccountBalanceWalletIcon sx={{ fontSize: 22 }} />
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton 
                selected={isFinanceiroMenuActive(activePath)}
                component={RouterLink}
                to="/financeiro"
                sx={{ 
                  width: '100%',
                  mx: 0
                }}
              >
                <AccountBalanceWalletIcon />
                <ListItemContent>
                  <Typography level="title-sm">Financeiro</Typography>
                </ListItemContent>
              </ListItemButton>
            )}
          </ListItem>
          

        </List>
        
        {/* Itens de navegação do rodapé */}
        <List
          size="sm"
          sx={{
            mt: 'auto',
            flexGrow: 0,
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
            '--List-gap': collapsed ? '16px' : '8px',
            mb: 2,
            width: '100%',
            px: 0,
            pt: 0,
            pb: collapsed ? 1 : 0,
            ...(collapsed && {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }),
          }}
        >
          <ListItem 
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: collapsed ? 0 : undefined,
              px: 0,
              overflow: 'visible',
            }}
          >
            {collapsed ? (
              <Tooltip
                title="Suporte"
                placement="right"
                variant="soft"
                className="dark-sidebar-tooltip"
                sx={tooltipStyle}
              >
                <ListItemButton
                  className="footer-button"
                  sx={buttonWithFullHover}
                  component={RouterLink}
                  to="/suporte"
                  selected={activePath === 'suporte'}
                >
                  <SupportRoundedIcon sx={{ fontSize: 22 }} />
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton 
                className="footer-button"
                sx={{ 
                  width: '100%',
                  mx: 0
                }}
                component={RouterLink}
                to="/suporte"
                selected={activePath === 'suporte'}
              >
                <SupportRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Suporte</Typography>
                </ListItemContent>
              </ListItemButton>
            )}
          </ListItem>
          <ListItem 
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              p: collapsed ? 0 : undefined,
              px: 0,
              overflow: 'visible',
            }}
          >
            {collapsed ? (
              <Tooltip
                title="Configurações"
                placement="right"
                variant="soft"
                className="dark-sidebar-tooltip"
                sx={tooltipStyle}
              >
                <ListItemButton
                  className="footer-button"
                  sx={buttonWithFullHover}
                  component={RouterLink}
                  to="/configuracoes"
                  selected={activePath === 'configuracoes'}
                >
                  <SettingsRoundedIcon sx={{ fontSize: 22 }} />
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton 
                className="footer-button"
                sx={{ 
                  width: '100%',
                  mx: 0
                }}
                component={RouterLink}
                to="/configuracoes"
                selected={activePath === 'configuracoes'}
              >
                <SettingsRoundedIcon />
                <ListItemContent>
                  <Typography level="title-sm">Configurações</Typography>
                </ListItemContent>
              </ListItemButton>
            )}
          </ListItem>
        </List>
      </Box>
      
      {/* Seção de perfil do usuário */}
      <Divider />
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        overflow: 'visible'
      }}>
        {!collapsed && (
          <>
            <Avatar
              variant="outlined"
              size="sm"
              src={
                currentUser?.profile_picture_thumb_url || 
                currentUser?.profile_picture_url || 
                undefined
              }
              alt={currentUser?.first_name || 'Usuário'}
            />
            <Box sx={{ minWidth: 0, flex: 1, ml: 1 }}>
              <Typography level="title-sm">
                {currentUser ? 
                  `${currentUser.first_name || ''} ${currentUser.last_name || ''}` : 
                  'Usuário'}
              </Typography>
              <Typography level="body-xs">
                {currentUser?.username ? `@${currentUser.username}` : ''}
              </Typography>
            </Box>
          </>
        )}
        {collapsed ? (
          <Tooltip
            title="Sair"
            placement="right"
            variant="soft"
            className="dark-sidebar-tooltip"
            sx={tooltipStyle}
          >
            <IconButton 
              size="md" 
              variant="plain" 
              color="neutral"
              sx={buttonWithFullHover}
              onClick={logout}
            >
              <LogoutRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton size="sm" variant="plain" color="neutral" onClick={logout}>
            <LogoutRoundedIcon />
          </IconButton>
        )}
      </Box>
    </Sheet>
  );
}
